import { useMemo, useState } from 'react'
import { useCurrency } from '@/hooks/useCurrency'
import { DataTable, Pill, ReportPage, StatGrid, useReport, exportCsv, type Column, type Tone } from '@/components/reports/ReportKit'

type ItemClass = 'Star' | 'Plowhorse' | 'Puzzle' | 'Dog' | null

interface MenuItemRow {
  name: string
  category: string
  quantity: number
  revenue: number
  avgPrice: number
  unitMargin: number | null
  profit: number | null
  class: ItemClass
}

interface MenuData {
  items: MenuItemRow[]
  summary: { itemCount: number; totalQuantity: number; totalRevenue: number; classified: number }
}

const CLASS_TONE: Record<string, Tone> = { Star: 'good', Plowhorse: 'warn', Puzzle: 'muted', Dog: 'bad' }
const CLASS_HINT: Record<string, string> = {
  Star: 'Popular and profitable — protect and promote',
  Plowhorse: 'Popular but low margin — review price or cost',
  Puzzle: 'Profitable but slow — reposition or promote',
  Dog: 'Slow and low margin — candidates to drop'
}

export default function MenuPerformanceScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<MenuData>('menu-performance')
  const [view, setView] = useState<'all' | 'top' | 'slow'>('all')
  const data = report.data

  const rows = useMemo(() => {
    const items = data?.items ?? []
    if (view === 'top') return [...items].sort((a, b) => b.quantity - a.quantity).slice(0, 10)
    if (view === 'slow') return [...items].sort((a, b) => a.quantity - b.quantity).slice(0, 10)
    return items
  }, [data, view])

  const counts = useMemo(() => {
    const c: Record<string, number> = { Star: 0, Plowhorse: 0, Puzzle: 0, Dog: 0 }
    for (const i of data?.items ?? []) if (i.class) c[i.class]++
    return c
  }, [data])

  const columns: Column<MenuItemRow>[] = [
    { key: 'name', header: 'Item', render: r => <span className="font-medium">{r.name}</span> },
    { key: 'category', header: 'Category' },
    { key: 'quantity', header: 'Qty sold', align: 'right' },
    { key: 'revenue', header: 'Revenue', align: 'right', render: r => formatCurrency(r.revenue) },
    { key: 'unitMargin', header: 'Margin / unit', align: 'right', render: r => (r.unitMargin === null ? '—' : formatCurrency(r.unitMargin)), value: r => r.unitMargin ?? -1 },
    { key: 'profit', header: 'Profit', align: 'right', render: r => (r.profit === null ? '—' : formatCurrency(r.profit)), value: r => r.profit ?? -1 },
    { key: 'class', header: 'Class', render: r => (r.class ? <Pill tone={CLASS_TONE[r.class]}>{r.class}</Pill> : <span className="text-muted-foreground">—</span>), value: r => r.class ?? '' }
  ]

  return (
    <ReportPage
      eyebrow="Sales · Menu engineering"
      title="Menu Performance"
      subtitle="Best and slowest sellers, with Stars / Plowhorses / Puzzles / Dogs once item cost is filled in"
      report={report}
      onExport={() => exportCsv('menu-performance', columns, data?.items ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Items sold', value: data.summary.itemCount },
              { label: 'Quantity sold', value: data.summary.totalQuantity },
              { label: 'Item revenue', value: formatCurrency(data.summary.totalRevenue) },
              { label: 'Items with cost', value: `${data.summary.classified} / ${data.summary.itemCount}`, sub: 'Needed to classify items' }
            ]}
          />

          {data.summary.classified > 0 && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {(['Star', 'Plowhorse', 'Puzzle', 'Dog'] as const).map(k => (
                <div key={k} className="rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <Pill tone={CLASS_TONE[k]}>{k}s</Pill>
                    <span className="text-xl font-bold">{counts[k]}</span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">{CLASS_HINT[k]}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            {([['all', 'All items'], ['top', 'Top 10 sellers'], ['slow', 'Slowest 10']] as const).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={`rounded-full border px-3 py-1.5 text-sm ${view === k ? 'bg-secondary font-medium' : 'text-muted-foreground'}`}
              >
                {label}
              </button>
            ))}
          </div>

          <DataTable
            columns={columns}
            rows={rows}
            rowKey={r => r.name}
            defaultSort={view === 'slow' ? 'quantity' : 'revenue'}
            csvName="menu-performance"
            empty="No items sold in this period."
          />
        </>
      )}
    </ReportPage>
  )
}
